import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { showDangerToast, showToast } from '../../constants/toaster';
import { updateLoading } from '../../../redux/features/common/commonSlice';
import urls from '../../constants/apiurl';
import ApiCaller from '../../constants/ApiCaller';

const CategoryMappingModal = ({ show, handleClose, productData, onSuccess }) => {
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const userData = useSelector(state => state?.userData);
    const LOADING_ENABLE = useSelector(state => state?.loading);

    // Fetch categories when modal opens
    useEffect(() => {
        if (show) {
            fetchCategories();
            // Set current category if product has one
            if (productData?.categoryDetails?.id) {
                setSelectedCategory(productData.categoryDetails.id);
            } else {
                setSelectedCategory('');
            }
        }
    }, [show, productData]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const path = urls.productCategoryKeyValuePair;
            const res = await ApiCaller.get({}, path, userData?.access_token);
            const response = await res.response.json();

            if (response.status === 200 || response.status === '200') {
                setCategories(response.data || []);
            } else {
                showDangerToast(response.message || 'Failed to fetch categories');
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            showDangerToast(error.message || 'Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedCategory) {
            showDangerToast('Please select a category');
            return;
        }
        if (!productData?.uuid) {
            showDangerToast('Product ID is missing');
            return;
        }
        try {
            dispatch(updateLoading(true));
            const path = urls.updateProductCategory;
            const postJson = {
                mapProductCategoryList: [{
                    productId: productData.uuid,
                    categoryId: selectedCategory
                }
                ]
            };
            const res = await ApiCaller.put(postJson, path, userData?.access_token);
            const response = await res.response.json();

            if (response.status === 200 || response.status === '200') {
                dispatch(updateLoading(false));
                showToast(response?.message || 'Category updated successfully');
                handleClose();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                dispatch(updateLoading(false));
                showDangerToast(response.message || 'Failed to update category');
            }
        } catch (error) {
            dispatch(updateLoading(false));
            console.error('Error updating category:', error);
            showDangerToast(error.message || 'Failed to update category');
        }
    };

    const handleModalClose = () => {
        setSelectedCategory('');
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleModalClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Map Category to Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <strong>Product Name:</strong> {productData?.itemName || '-'}
                </div>

                <Form.Group>
                    <Form.Label>Select Category</Form.Label>
                    <Form.Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        disabled={loading || LOADING_ENABLE}
                    >
                        <option value="">Choose a category...</option>
                        {categories.map((category) => (
                            <option key={category.key} value={category.key}>
                                {category.value}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {loading && (
                    <div className="text-center mt-3">
                        <div className="spinner-border spinner-border-sm" role="status">
                            <span className="sr-only">Loading categories...</span>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={handleModalClose}
                    disabled={LOADING_ENABLE}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={loading || LOADING_ENABLE || !selectedCategory}
                >
                    {LOADING_ENABLE ? (
                        <>
                            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                            Updating...
                        </>
                    ) : (
                        'Update Category'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CategoryMappingModal;
